import { ClipboardCheck, LogOut, Settings, UserPlus } from "lucide-react";
import type { NavSection } from "@skypointcloud/skypoint-ui";
import { PRODUCT_LABELS } from "../../../shared/products/keys";

/**
 * HRIS's sidebar (2026-11-05): Onboarding, Offboarding, Approvals and its own Settings. HRIS has
 * no roles of its own, so who sees what is decided by the caller's Hire permissions (the same ones
 * the Hire sidebar used for Onboarding) plus the instance having HRIS. Ids are stable keys the
 * route table and the route guard index on.
 *
 * PURE: no store, no RTK, so the shell (sidebarConfig.ts) can call it and a test can too.
 */
export const HRIS_NAV_IDS = {
  HRIS_ONBOARDING: "hris-onboarding",
  HRIS_OFFBOARDING: "hris-offboarding",
  HRIS_APPROVALS: "hris-approvals",
  HRIS_SETTINGS: "hris-settings",
} as const;
export type HrisNavId = (typeof HRIS_NAV_IDS)[keyof typeof HRIS_NAV_IDS];

export interface HrisNavContext {
  /** The caller holds a Hire role on an instance that has HRIS (the session's productRoles). */
  held: boolean;
  /** Applicants are in the caller's navigation (the cases are scoped to their candidate access). */
  canView: boolean;
  /** An admin: the templates and activation steps are theirs to change. */
  canManageSettings: boolean;
}

/** HRIS's sections, or none for someone without standing (the guard then admits no HRIS page). */
export function buildHrisNavSections(ctx: HrisNavContext): NavSection[] {
  if (!ctx.held || !ctx.canView) return [];
  const items: NavSection["items"] = [
    { id: HRIS_NAV_IDS.HRIS_ONBOARDING, label: "Onboarding", icon: UserPlus },
    { id: HRIS_NAV_IDS.HRIS_OFFBOARDING, label: "Offboarding", icon: LogOut },
    { id: HRIS_NAV_IDS.HRIS_APPROVALS, label: "Approvals", icon: ClipboardCheck },
  ];
  if (ctx.canManageSettings) {
    items.push({ id: HRIS_NAV_IDS.HRIS_SETTINGS, label: "Settings", icon: Settings });
  }
  return [{ header: PRODUCT_LABELS.hris, items }];
}
